import getMatchingVariant from '../utils/variants/getMatchingVariant';
import updateVariantAvailability from '../utils/variants/updateVariantAvailability';
import formatMoney from '../utils/formatMoney';
import toggleVisibility from '../utils/toggleVisibility';
import safeJSONParse from '../utils/safeJsonParse';
import { ATTRIBUTES, SELECT_OPTIONS } from '../utils/constants';

// PDP "Select Option" boxes (snippets/product-select-options.liquid): the page
// product's box plus one per linked product. A linked box's variant replaces
// the page variant in the form's hidden id input, so the one add-to-bag
// button (and the sticky bar that mirrors it) adds that product instead.
// The page product's own picker (Size above the boxes, Colour inside its box)
// stays product-variants.js's: any change there wins the selection back.

const parseJson = (jsonEl) => safeJSONParse(jsonEl?.textContent, []);

const getThumbnailUrl = (src) => {
  if (!src) return null;
  const url = new URL(src, window.location.origin);
  url.searchParams.set('width', SELECT_OPTIONS.THUMBNAIL_SIZE);
  url.searchParams.set('height', SELECT_OPTIONS.THUMBNAIL_SIZE);
  url.searchParams.set('crop', SELECT_OPTIONS.THUMBNAIL_CROP);
  return url.toString();
};

const fillTemplate = (template, key, value) => (template || `{{ ${key} }}`).replace(`{{ ${key} }}`, value);

const initProductSelectOptions = (container = document.querySelector('[data-product-main]')) => {
  const rootEl = container?.querySelector('[data-select-options]');
  const formEl = container?.querySelector('[data-product-form]');
  if (!rootEl || !formEl) return;
  const idInputEl = formEl.querySelector('[data-atc-id-input]');
  const atcButtonEl = formEl.querySelector('button[type="submit"]');
  const pageBoxInputEl = rootEl.querySelector(`${SELECT_OPTIONS.BOX_INPUT_SELECTOR}[data-select-option-page]`);
  if (!idInputEl || !atcButtonEl || !pageBoxInputEl) return;

  const atcLabelEl = atcButtonEl.querySelector('[data-atc-price-msg]');
  const quantityControlEls = formEl.querySelectorAll('[data-quantity-selector] button, [data-quantity-selector] input');
  const quantityInputEl = formEl.querySelector(SELECT_OPTIONS.QUANTITY_INPUT_SELECTOR);
  // The page product's own messaging (updateSelectedVariants.js owns it):
  // hidden while a linked box drives the form, so a sold-out page variant's
  // back-in-stock form never sits under an "Add to bag" button
  const pageMessageEls = [
    container.querySelector('[data-product-preorder-msg]'),
    container.querySelector('[data-product-sold-info]'),
    container.querySelector('[data-klaviyo-bis]')
  ].filter(Boolean);
  const deliveryCountdownEl = container.querySelector('[data-delivery-countdown]');
  const titleEl = rootEl.querySelector('[data-select-option-selected-title]');
  const statusEl = rootEl.querySelector('[data-select-option-status]');
  const boxInputEls = rootEl.querySelectorAll(SELECT_OPTIONS.BOX_INPUT_SELECTOR);
  const pageVariants = parseJson(formEl.querySelector('[data-product-variants-json]'));
  const strings = window.theme?.strings || {};
  const moneyFormat = window.theme?.moneyFormat;
  const boxDataCache = new WeakMap();
  // Liquid-rendered button state, restored for a default-variant page product
  // (no picker to re-fire; keeps its pre-order / sold-out label)
  const initialAtcState = { isAvailable: !atcButtonEl.disabled, label: atcLabelEl?.textContent.trim() };
  const initialMessageState = {
    visible: pageMessageEls.map((messageEl) => !messageEl.classList.contains('hidden')),
    isCountdownSoldOut: Boolean(deliveryCountdownEl?.hasAttribute(ATTRIBUTES.VARIANT_SOLD_OUT))
  };
  let pageVariantId = idInputEl.value;
  // The linked box currently driving the form, null while the page product is selected
  let activeBoxEl = null;

  const getBoxEl = (childEl) => childEl.closest(SELECT_OPTIONS.BOX_SELECTOR);

  const getBoxData = (boxEl) => {
    if (boxDataCache.has(boxEl)) return boxDataCache.get(boxEl);
    const data = {
      variants: parseJson(boxEl.querySelector('[data-select-option-variants]')),
      optionNames: parseJson(boxEl.querySelector('[data-select-option-options]'))
    };
    boxDataCache.set(boxEl, data);
    return data;
  };

  const getSelectedOptions = (boxEl) => {
    const selectedOptions = {};
    boxEl.querySelectorAll(`${SELECT_OPTIONS.OPTION_INPUT_SELECTOR}:checked`).forEach((inputEl) => {
      selectedOptions[inputEl.dataset.optionName] = inputEl.value;
    });
    return selectedOptions;
  };

  const resolveBoxVariant = (boxEl) => {
    const { variants, optionNames } = getBoxData(boxEl);
    if (!variants.length) return null;
    return getMatchingVariant(getSelectedOptions(boxEl), variants, optionNames) || null;
  };

  // Keeps a linked box's strike-through / sold-out markers true to its own
  // current selection (the page picker's pass skips these radios)
  const updateBoxAvailability = (boxEl) => {
    const { variants, optionNames } = getBoxData(boxEl);
    if (!optionNames.length) return;
    updateVariantAvailability({
      selectedOptions: getSelectedOptions(boxEl),
      variantsData: variants,
      optionNames,
      form: boxEl,
      strings,
      inputSelector: SELECT_OPTIONS.OPTION_INPUT_SELECTOR
    });
  };

  // The "(£x Value)" line is product-level (a metafield), so only the price
  // and thumbnail follow the variant; a variant without its own image keeps
  // the box's first-render image
  const renderBoxVariant = (boxEl, variant) => {
    const priceEl = boxEl.querySelector('[data-select-option-price]');
    const imageEl = boxEl.querySelector('[data-select-option-image]');
    if (priceEl) priceEl.textContent = formatMoney(variant.price, moneyFormat);
    if (!imageEl) return;
    imageEl.dataset.defaultSrc ||= imageEl.src;
    imageEl.src = getThumbnailUrl(variant.featured_image?.src) || imageEl.dataset.defaultSrc;
  };

  const setAtcState = ({ isAvailable, label }) => {
    atcButtonEl.disabled = !isAvailable;
    quantityControlEls.forEach((controlEl) => { controlEl.disabled = !isAvailable; });
    if (atcLabelEl) atcLabelEl.textContent = label;
  };

  const hidePageMessages = (isLinkedAvailable) => {
    pageMessageEls.forEach((messageEl) => toggleVisibility(messageEl, false));
    deliveryCountdownEl?.toggleAttribute(ATTRIBUTES.VARIANT_SOLD_OUT, !isLinkedAvailable);
  };

  const restorePageMessages = () => {
    pageMessageEls.forEach((messageEl, index) => toggleVisibility(messageEl, initialMessageState.visible[index]));
    deliveryCountdownEl?.toggleAttribute(ATTRIBUTES.VARIANT_SOLD_OUT, initialMessageState.isCountdownSoldOut);
  };

  // "Add to bag - £45" (Figma 1781-4974): the linked variant's price times the quantity
  const getLinkedLabel = (variant) => {
    const quantity = Number(quantityInputEl?.value) || 1;
    return fillTemplate(strings.selectOptionAddToBag, 'price', formatMoney(variant.price * quantity, moneyFormat));
  };

  const applyLinkedVariant = (variant) => {
    idInputEl.value = variant.id;
    setAtcState({
      isAvailable: Boolean(variant.available),
      label: variant.available ? getLinkedLabel(variant) : strings.soldOut
    });
    hidePageMessages(Boolean(variant.available));
  };

  const selectLinkedBox = (boxEl) => {
    activeBoxEl = boxEl;
    updateBoxAvailability(boxEl);
    const variant = resolveBoxVariant(boxEl);
    if (!variant) {
      setAtcState({ isAvailable: false, label: strings.unavailable });
      hidePageMessages(false);
      return;
    }
    applyLinkedVariant(variant);
    renderBoxVariant(boxEl, variant);
  };

  const refreshLinkedLabel = () => {
    if (!activeBoxEl) return;
    const variant = resolveBoxVariant(activeBoxEl);
    if (variant?.available && atcLabelEl) atcLabelEl.textContent = getLinkedLabel(variant);
  };

  // Re-firing the page picker's checked radio lets product-variants.js
  // restore everything it owns (button label, pre-order state, qty controls);
  // a default-variant page product has no picker, so its Liquid state is
  // put back directly
  const restorePageProduct = () => {
    activeBoxEl = null;
    idInputEl.value = pageVariantId;
    const checkedEl = formEl.querySelector(`${SELECT_OPTIONS.PAGE_INPUT_SELECTOR}:checked`);
    if (checkedEl) {
      checkedEl.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    setAtcState(initialAtcState);
    restorePageMessages();
  };

  const setPanels = (activeInputEl) => {
    boxInputEls.forEach((inputEl) => {
      const panelEl = getBoxEl(inputEl)?.querySelector(SELECT_OPTIONS.PANEL_SELECTOR);
      panelEl?.toggleAttribute(ATTRIBUTES.INERT, inputEl !== activeInputEl);
    });
  };

  const setSelectedTitle = (inputEl) => {
    if (titleEl) titleEl.textContent = inputEl.dataset.selectOptionTitle || '';
  };

  const onBoxChange = (inputEl) => {
    setPanels(inputEl);
    setSelectedTitle(inputEl);
    if (inputEl === pageBoxInputEl) {
      restorePageProduct();
      return;
    }
    selectLinkedBox(getBoxEl(inputEl));
  };

  const onLinkedOptionChange = (inputEl) => {
    const boxEl = getBoxEl(inputEl);
    if (!boxEl) return;
    const optionName = inputEl.dataset.optionName || '';
    const selectedValueEl = boxEl.querySelector(`[data-select-option-selected-value="${CSS.escape(optionName)}"]`);
    if (selectedValueEl) selectedValueEl.textContent = inputEl.value;
    selectLinkedBox(boxEl);
  };

  const onRootChange = ({ target }) => {
    if (target.matches(SELECT_OPTIONS.BOX_INPUT_SELECTOR)) onBoxChange(target);
    else if (target.matches(SELECT_OPTIONS.OPTION_INPUT_SELECTOR)) onLinkedOptionChange(target);
  };

  // Runs after product-variants.js's own listener on the radio, so the hidden
  // input already holds the page product's new variant. This is the one
  // selection change the user didn't make on a box, so it is the one announced.
  const onPagePickerChange = ({ target }) => {
    if (!target.matches(SELECT_OPTIONS.PAGE_INPUT_SELECTOR)) return;
    activeBoxEl = null;
    pageVariantId = idInputEl.value;
    const variant = pageVariants.find((candidate) => String(candidate.id) === String(pageVariantId));
    if (variant) renderBoxVariant(getBoxEl(pageBoxInputEl), variant);
    if (pageBoxInputEl.checked) return;
    pageBoxInputEl.checked = true;
    setPanels(pageBoxInputEl);
    setSelectedTitle(pageBoxInputEl);
    if (statusEl) {
      statusEl.textContent = fillTemplate(strings.selectOptionSelected, 'title', pageBoxInputEl.dataset.selectOptionTitle || '');
    }
  };

  // The stepper buttons change the input silently (quantityInputs.js), so
  // their clicks are watched as well as typed changes
  const onQuantityChange = ({ target }) => {
    if (target.matches(SELECT_OPTIONS.QUANTITY_INPUT_SELECTOR)) refreshLinkedLabel();
  };
  const onQuantityClick = ({ target }) => {
    if (target.closest('[data-quantity-dec], [data-quantity-inc]')) refreshLinkedLabel();
  };

  rootEl.addEventListener('change', onRootChange);
  formEl.addEventListener('change', onPagePickerChange);
  formEl.addEventListener('change', onQuantityChange);
  formEl.addEventListener('click', onQuantityClick);
};

export default initProductSelectOptions;
